import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { deleteDocument, getDocumentNames, putDocuments } from '../api/documents';
import Button from '@mui/material/Button';
import Grid2 from '@mui/material/Grid2';
import { AlertView } from './alert';
import { getApiAccessToken } from '../auth/auth';
import { useMsal } from '@azure/msal-react';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import { Program } from '../model/program';
import { Loader } from './loader';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/store';
import TextField from '@mui/material/TextField';
import { Badge, Box } from '@mui/material';

export interface DocumentProps {
    updateSystemPrompt: (programId: string, systemPrompt: string) => void
}

export const DocumentsUploadView = ({updateSystemPrompt}: DocumentProps) => {
	const { instance } = useMsal();
	const activeAccount = instance.getActiveAccount();

	const currentProgram: Program | null = useSelector((state: RootState) => state.programs.currentProgram);
    const [programSystemPrompt, setProgramSystemPrompt] = useState<string | null>(currentProgram?.systemPrompt || '');

	const MAX_TOKEN_LENGTH = 122000

	// Simple approximation: ~4 characters per token for English text
	const countTokens = (text: string): number => {
		return Math.ceil(text.length / 4);
	}

	const currentTokens = programSystemPrompt ? countTokens(programSystemPrompt) : 0;
	const isOverLimit = currentTokens > MAX_TOKEN_LENGTH;
	

	const [existingFiles, setExistingFiles] = useState<string[]>([]);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [loadingFiles, setLoadingFiles] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [errorMessage, setError] = useState<string | null>(null);
	const [isHovered, setIsHovered] = useState(false);
	
	const handleSystemPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newPrompt = event.target.value;
		setProgramSystemPrompt(newPrompt);
	};

	// add debounce in ms
	useEffect(() => {
		const handler = setTimeout(() => {
			if (!programSystemPrompt || programSystemPrompt?.length > MAX_TOKEN_LENGTH) {
				return
			}

			if (currentProgram && currentProgram.systemPrompt != programSystemPrompt) {
				updateSystemPrompt(currentProgram.id, programSystemPrompt);
			}
		}, 500);

		return () => {
			clearTimeout(handler);
		};
	}, [programSystemPrompt]);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		setSelectedFiles(prev => {
		const allFiles = [...prev, ...acceptedFiles];

		// Remove duplicates by name and size
		const uniqueFiles = Array.from(
			new Map(allFiles.map(file => [`${file.name}-${file.size}`, file])).values()
		);
		return uniqueFiles;
		});
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			'application/pdf': ['.pdf'],
			'text/plain': ['.txt'],
		},
		multiple: true,
	});

	// Fetch uploaded files
	const fetchExistingFiles = useCallback(async () => {
		if (currentProgram?.id == undefined) 
		return;

		setLoadingFiles(true);

		try {
			let accessToken: string = await getApiAccessToken(instance, activeAccount);
			const files = await getDocumentNames(accessToken, currentProgram?.id);
			setExistingFiles(files);
		} catch (error) {
		if (error instanceof Error) {
			if (error.message.includes('404')) {
				// If 404, no files exist yet, so we can just set an empty array
				setExistingFiles([]);
			} else {
				console.error('Error fetching existing files:', error);
				setError(error.message);
			}
		}
		} finally {
		setLoadingFiles(false);
		}
	}, [currentProgram]);

	React.useEffect(() => {
		setExistingFiles([]);
		fetchExistingFiles();
		setProgramSystemPrompt(currentProgram?.systemPrompt || '');
	}, [currentProgram]);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
		setSelectedFiles(prev => {
			const allFiles = [...prev, ...Array.from(e.target.files)];
			// Remove duplicates by name and size
			const uniqueFiles = Array.from(
			new Map(allFiles.map(file => [`${file.name}-${file.size}`, file])).values()
			);
			return uniqueFiles;
		});
		}
	};

	const handleUpload = async () => {
		setUploading(true);
		setError(null);
		
		try {
		if (currentProgram?.id == undefined) {
			setError('Program ID is required');
			return
		}
		
		let accessToken: string = await getApiAccessToken(instance, activeAccount)

		await putDocuments(accessToken, currentProgram.id, selectedFiles);
		setSelectedFiles([]);

		// refetch after successful upload
		await fetchExistingFiles(); 
		
		} catch (err: any) {
			setError('Upload failed');
		} finally {
			setUploading(false);
		}
	};
	
	const handleDelete = async (fileName: string) => {
		if (!currentProgram?.id) {
		setError('Program ID is required');
		return;
		}

		try {
		let accessToken: string = await getApiAccessToken(instance, activeAccount)
		await deleteDocument(accessToken, currentProgram.id, fileName);
		setExistingFiles(files => files.filter(f => f !== fileName));

		} catch (err) {
		setError('Delete failed');
		}
	};

	return (
		<div style={{padding: 16, textAlign: 'left' }}>
		<Badge 
			badgeContent="!" 
			color="error"
			invisible={!isOverLimit}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'left',
			}}
			sx={{ 
				width: '100%',
				'& .MuiBadge-badge': {
				transform: 'translate(-10px, 10px)',
				}
			}}
		>
			<TextField
				label="Program System Prompt"
				placeholder="The prompt that's used by the LLM that will answer any user questions on a program level"
				multiline
				rows={4}
				fullWidth
				value={programSystemPrompt || ''}
				onChange={handleSystemPromptChange}
				variant="outlined"
				sx={{ marginBottom: 2 }}
				helperText={
					<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
						<span>
							{
								isOverLimit ? 
								`Program system prompt exceeds limit by ${currentTokens - MAX_TOKEN_LENGTH}` : 
								''
							}
						</span>
						<Typography 
							variant="caption" 
							color={isOverLimit ? 'error' : 'text.secondary'}
						>
							~{currentTokens}/{MAX_TOKEN_LENGTH} tokens
						</Typography>
					</Box>
				}
			/>
    	</Badge>

		<Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'gray' }}>
			Documents are used in AI chat to provide context for answers
		</Typography>
		<Stack 
			direction="row" 
			spacing={2} 
			sx={{
			flexWrap: 'wrap',
			gap: 1,
			mb: 2,
			alignItems: 'flex-start',
			justifyContent: 'flex-start', // align chips to the left
			}}>
			{
			loadingFiles ? <Loader /> : (
				existingFiles.map((file) => (
				<Chip
					key={file}
					label={file}
					size="small"
					onDelete={() => handleDelete(file)}
					sx={{ m: 1 }}
				/>
				))
			)
			}
		</Stack>

		<div
			{...getRootProps()}
			style={{
			border: '2px dashed #888',
			padding: 24,
			textAlign: 'center',
			marginBottom: 16,
			background: isDragActive || isHovered ? '#f3f3f3ff': '#fff',
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<input {...getInputProps()} />
			{
			isHovered ?
				<p>Click to select files.</p>
			: isDragActive
				? <p>Drop the files here ...</p>
				: <p>Drag & drop .txt or .pdf files here, or click to select files</p>
			}
		</div>
		
		<input
			type="file"
			accept=".pdf,.txt"
			multiple
			style={{ display: 'none' }}
			onChange={handleFileChange}
		/>

		<Grid2 container direction="row" alignContent={'left'}>
			<Button
				variant="outlined" 
				sx={{ minWidth: 300, maxHeight: 50, alignSelf: 'left' }}
				fullWidth={true}
				color="primary" 
				size="large"
				type="button"
				onClick={handleUpload}
				disabled={uploading || selectedFiles.length === 0}
			>
			{uploading ? 'Uploading...' : 'Upload'}
			</Button>

			{selectedFiles.length > 0 && (
			<div style={{ marginTop: 16, textAlign: 'left'}}>
				<strong >Files to upload:</strong>
				<ul style={{ textAlign: 'left', paddingLeft: 20 }}>
				{selectedFiles.map((file, idx) => (
					<li key={idx}>{file.name}</li>
				))}
				</ul>
			</div>
			)}
		</Grid2>

		<AlertView error={errorMessage ? `${errorMessage?.substring(0, 200)}...` : null}/>
		</div>
	);
};